import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { FdaRecallModel } from '../../../../models/data-sections/fda/recallModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface RecallModalProps {
  searchExecutionId: string;
  selected_recall: FdaRecallModel;
}

const RecallModal: React.FC<RecallModalProps> = ({
  searchExecutionId,
  selected_recall,
}) => {
  const [recallData, setRecallData] = useState<FdaRecallModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchRecallDetail(searchExecutionId, selected_recall.product_res_number, new Date(selected_recall.__ci_system_search_date_key ?? 0));
      } catch (error) {
        console.error('Error during fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_recall]);

  const fetchRecallDetail = async (
    search_execution_id: string,
    product_res_number: string,
    __ci_system_search_date_key: Date
  ): Promise<void> => {
    try {
      const response = await fdaApi.getRecallDetail(
        search_execution_id,
        product_res_number,
        __ci_system_search_date_key
      );

      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaRecallModel[];
        setRecallData(data?.[0] || null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          null,
          setDidErrorOccur,
          triggerSystemError
        );
      }
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        null,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1} mb={2}>
        <Typography variant="h6">
          {recallData?.product_description || 'Recall Details'}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          {selected_recall.product_res_number} / {selected_recall.recalling_firm}
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : didErrorOccur || !recallData ? (
        <Typography color="error">Failed to load recall details.</Typography>
      ) : (
        <Box mt={2}>
          <Typography><strong>Status:</strong> {recallData.recall_status || 'N/A'}</Typography>
          <Typography><strong>Reason:</strong> {recallData.reason_for_recall || 'N/A'}</Typography>
          <Typography><strong>Initiated:</strong> {recallData.event_date_initiated || 'N/A'}</Typography>
          <Typography><strong>Posted:</strong> {recallData.event_date_posted || 'N/A'}</Typography>
          <Typography><strong>Quantity:</strong> {recallData.product_quantity || 'N/A'}</Typography>
          <Typography><strong>Root Cause:</strong> {recallData.root_cause_description || 'N/A'}</Typography>
          <Typography><strong>Distribution:</strong> {recallData.distribution_pattern || 'N/A'}</Typography>

          {recallData.code_info && (
            <Box mt={3}>
              <Typography variant="h6">Code Info</Typography>
              <Typography paragraph>{recallData.code_info}</Typography>
            </Box>
          )}

          {recallData.action && (
            <Box mt={3}>
              <Typography variant="h6">Action</Typography>
              <Typography paragraph>{recallData.action}</Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default RecallModal;
